// src/encounter/services/use-wearable-trends.ts
// The patient's device (wearable) trends for the encounter's chart column. Reads nothing unless the `wearables` switch
// is on: ON by default on the dev hosts only, `?x=-wearables` hides it. A 403 hides the section.
//
// The server is the control: every staff read checks the patient's `wearable-sharing` consent and answers 403 without
// it. A 403 here means "do not render the section" (ruling, 30 Oct 2026: no consent or no connection = no section). It is
// never shown as an error, and it is never a statement about the patient's health.
import { useQuery } from '@tanstack/react-query';
import { ApiError, get } from '@/shared/api/api-client';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import type { WearableConnectionWire, WearableDailyWire, WearableSleepWire } from '@/types/wearable-api';
import { WEARABLE_WINDOW_DAYS, buildWearableTrends, clinicToday, windowDays, type WearableTrends } from '../lib/wearable-trends';

const base = (patientId: string) => `/api/Patients/${encodeURIComponent(patientId)}/wearable`;

function asArray<T>(res: unknown, what: string): T[] {
  // A non-array is a contract break: it must surface as a failed read, never as "no device data".
  if (!Array.isArray(res)) throw new Error(`wearable/${what}: response is not an array`);
  return res as T[];
}

/**
 * The server's from/to (UTC, inclusive; the server filters on StartTime) covering the 30 clinic days ending on `today`.
 * It starts a day early: a clinic day begins at 20:00Z the day before (UTC+4), and a night that ends on the first day
 * started the evening before. The transform keeps only the window's clinic days.
 */
export function wearableWindow(today: string): { from: string; to: string } {
  const days = windowDays(today, WEARABLE_WINDOW_DAYS + 1);
  return { from: `${days[0]}T00:00:00Z`, to: `${days[days.length - 1]}T23:59:59Z` };
}

export async function fetchWearableTrends(patientId: string, today: string, signal?: AbortSignal): Promise<WearableTrends> {
  try {
    const connections = asArray<WearableConnectionWire>(await get<unknown>(`${base(patientId)}/connections`, { signal }), 'connections');
    if (!connections.some((c) => !c.disconnectedAt)) return { kind: 'hidden' };

    const { from, to } = wearableWindow(today);
    const range = `from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    const [daily, sleep] = await Promise.all([
      get<unknown>(`${base(patientId)}/daily?${range}`, { signal }).then((r) => asArray<WearableDailyWire>(r, 'daily')),
      get<unknown>(`${base(patientId)}/sleep?${range}`, { signal }).then((r) => asArray<WearableSleepWire>(r, 'sleep')),
    ]);
    return buildWearableTrends({ connections, daily, sleep, today });
  } catch (err) {
    // No consent to share (403): the section does not render. Any other failure stays a failure.
    if (err instanceof ApiError && err.status === 403) return { kind: 'hidden' };
    throw err;
  }
}

export const wearableTrendsKey = (patientId?: string | null, today?: string) => ['wearable-trends', patientId ?? null, today ?? null] as const;


/**
 * `{ kind: 'off' }` while the switch is off or there is no patient (nothing is read). Otherwise the react-query
 * result of `fetchWearableTrends`: hidden (no consent / no connection), empty (consented, connected, nothing in 30
 * days) or ready. A failed read is `isError` on the query, distinct from all three.
 */
/** The `wearables` switch for this browser. Checked BEFORE mounting anything that reads, so an off switch costs nothing. */
export const isWearablesOn = () => isEncounterFlagOn('wearables');

export function useWearableTrends(patientId: string | null | undefined) {
  const on = isWearablesOn() && !!patientId;
  const today = clinicToday();
  const query = useQuery({
    queryKey: wearableTrendsKey(patientId, today),
    queryFn: ({ signal }) => fetchWearableTrends(patientId!, today, signal),
    enabled: on,
    staleTime: 5 * 60_000,
    retry: false,
  });
  return { on, ...query };
}
